import { useEffect, useRef, RefObject } from 'react'
import { DESKTOP_CARD_WIDTH, DESKTOP_CARD_HEIGHT } from './getCardDimensions'

const DEFAULT_DURATION_MS = 2000

/** Ease-in-out curve: 3t^2 - 2t^3 */
const easeInOut = (t: number): number => t * t * (3 - 2 * t)

interface Rect {
  top: number
  left: number
  width: number
  height: number
}

interface UseCardFlyAnimationOptions {
  active: boolean
  moverRef: RefObject<HTMLDivElement | null>
  flipperRef: RefObject<HTMLDivElement | null>
  onComplete: () => void
  durationMs?: number
}

/**
 * Drives a rAF animation loop that flies a card from its current position
 * to the `[data-oracle-card-landing]` element, interpolating position/size
 * and rotating the flipper from 0° to 180°.
 */
export const useCardFlyAnimation = ({
  active,
  moverRef,
  flipperRef,
  onComplete,
  durationMs = DEFAULT_DURATION_MS,
}: UseCardFlyAnimationOptions): void => {
  const rafRef = useRef<number | null>(null)
  const startTimeRef = useRef<number | null>(null)
  const startRectRef = useRef<Rect | null>(null)

  useEffect(() => {
    if (!active || !moverRef.current) return undefined

    const moverEl = moverRef.current
    const flipperEl = flipperRef.current

    // Capture start position from current screen location
    const rect = moverEl.getBoundingClientRect()
    startRectRef.current = {
      top: rect.top,
      left: rect.left,
      width: rect.width,
      height: rect.height,
    }
    startTimeRef.current = null

    const animate = (timestamp: number) => {
      if (!startTimeRef.current) {
        startTimeRef.current = timestamp
      }

      const elapsed = timestamp - startTimeRef.current
      const rawProgress = Math.min(elapsed / durationMs, 1)
      const progress = easeInOut(rawProgress)

      // Re-measure destination each frame to track autoscroll changes
      const landingEls = document.querySelectorAll('[data-oracle-card-landing]')
      const landingEl = landingEls.length > 0 ? landingEls[landingEls.length - 1] : null
      const start = startRectRef.current
      if (!start) return

      let endTop: number
      let endLeft: number
      let endWidth: number
      let endHeight: number

      if (landingEl) {
        const dest = landingEl.getBoundingClientRect()
        endTop = dest.top
        endLeft = dest.left
        endWidth = dest.width
        endHeight = dest.height
      } else {
        // Fallback: fly to center of screen at card dimensions
        endWidth = DESKTOP_CARD_WIDTH
        endHeight = DESKTOP_CARD_HEIGHT
        endTop = (window.innerHeight - endHeight) / 2
        endLeft = (window.innerWidth - endWidth) / 2
      }

      // Interpolate position and size
      const currentTop = start.top + (endTop - start.top) * progress
      const currentLeft = start.left + (endLeft - start.left) * progress
      const currentWidth = start.width + (endWidth - start.width) * progress
      const currentHeight = start.height + (endHeight - start.height) * progress

      moverEl.style.top = `${currentTop}px`
      moverEl.style.left = `${currentLeft}px`
      moverEl.style.width = `${currentWidth}px`
      moverEl.style.height = `${currentHeight}px`

      // Rotate flipper 0 → 180°
      if (flipperEl) {
        const rotation = 180 * progress
        flipperEl.style.transform = `rotateY(${rotation}deg)`
      }

      if (rawProgress < 1) {
        rafRef.current = requestAnimationFrame(animate)
      } else {
        onComplete()
      }
    }

    rafRef.current = requestAnimationFrame(animate)

    return () => {
      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current)
        rafRef.current = null
      }
    }
  }, [active, moverRef, flipperRef, onComplete, durationMs])
}
